import {useRef, useCallback, ChangeEvent, useEffect} from 'react';
import {useForm, Controller, SubmitHandler, useWatch} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import Yup from 'src/infra/Yup';
import {FieldSet} from 'src/components/fieldset';
import {FormGroup, Input, Label} from 'reactstrap';
import {useIntl} from 'react-intl';
import {createCompanySchema, updateCompanySchema} from './schemas';
import I18nMessage from 'src/components/I18nMessage';
import {useCreateCompany, useUpdateCompany} from './mutations';
import useAlerts from 'src/hooks/use-alerts';
import {useCompanyConfigState, useCompanyConfigDispatch} from './hooks';
import {useCompany} from 'src/providers/company';
import {TextInput} from 'src/autobahn/components/input/text-input';
import {Field} from 'src/autobahn/components/form/field';
import ColorPicker from 'src/autobahn/components/color-picker';
import Toggle from 'src/autobahn/components/toggle';
import Button from 'src/autobahn/components/button/button';
import Form from 'src/autobahn/components/form';
import {Central} from './rows';
import {useIntegrations} from 'src/autobahn/features/integration/queries';
import IntegrationAccessToggles from 'src/autobahn/features/integration/access-toggles';
import styles from './form.module.scss';

interface FormValues {
  id?: number;
  customerId?: number;
  label: string;
  customDomain: string;
  hideLoginFooter: boolean;
  theme: string;
  androidAppUrl: string;
  iosAppUrl: string;
  customerAreaLink: string;
  customerAreaContact: string;
  assistanceService: boolean;
  monitoringCenterService: boolean;
  monitoringCenterContact: string;
  attachment?: File;
}

interface CompanyFormViewProps {
  centrals: Central[];
}

const CompanyFormView = ({centrals}: CompanyFormViewProps) => {
  const currentCompany = useCompany();
  const {activeView, selectedCompany: stateSelectedCompany} = useCompanyConfigState();
  const dispatch = useCompanyConfigDispatch();

  const isMyCompany = activeView === 'my_company';
  const selectedCompany = isMyCompany ? currentCompany : stateSelectedCompany;

  const intl = useIntl();
  const {toast} = useAlerts();
  const {data: integrations} = useIntegrations();

  const {mutateAsync: createCompany, isPending: isCreatePending} = useCreateCompany();
  const {mutateAsync: updateCompany, isPending: isUpdatePending} = useUpdateCompany();

  const logoUrlRef = useRef<HTMLInputElement>(null);
  const attachmentRef = useRef<File | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  // A customer row with no configuration arrives with no id, same as a "my company" not created yet.
  const shouldCreate = !selectedCompany?.id;

  const schema = shouldCreate ? createCompanySchema : updateCompanySchema;

  const {
    control,
    handleSubmit,
    formState: {errors, isSubmitting},
    setValue,
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(schema as unknown as Yup.ObjectSchema<FormValues>),
    defaultValues: {
      id: selectedCompany?.id,
      customerId: selectedCompany?.customerId,
      label: selectedCompany?.label ?? '',
      customDomain: selectedCompany?.customDomain ?? '',
      hideLoginFooter: selectedCompany?.hideLoginFooter ?? false,
      theme: selectedCompany?.theme ?? '',
      androidAppUrl: selectedCompany?.androidAppUrl ?? '',
      iosAppUrl: selectedCompany?.iosAppUrl ?? '',
      customerAreaLink: selectedCompany?.customerAreaLink ?? '',
      customerAreaContact: selectedCompany?.customerAreaContact ?? '',
      assistanceService: selectedCompany?.assistanceService ?? false,
      monitoringCenterService: selectedCompany?.monitoringCenterService ?? false,
      monitoringCenterContact: selectedCompany?.monitoringCenterContact ?? '',
    },
  });

  useEffect(() => {
    if (selectedCompany) {
      reset({
        id: selectedCompany.id,
        customerId: selectedCompany.customerId,
        label: selectedCompany.label ?? '',
        customDomain: selectedCompany.customDomain ?? '',
        hideLoginFooter: selectedCompany.hideLoginFooter ?? false,
        theme: selectedCompany.theme ?? '',
        androidAppUrl: selectedCompany.androidAppUrl ?? '',
        iosAppUrl: selectedCompany.iosAppUrl ?? '',
        customerAreaLink: selectedCompany.customerAreaLink ?? '',
        customerAreaContact: selectedCompany.customerAreaContact ?? '',
        assistanceService: selectedCompany.assistanceService ?? false,
        monitoringCenterService: selectedCompany.monitoringCenterService ?? false,
        monitoringCenterContact: selectedCompany.monitoringCenterContact ?? '',
      });
    }
  }, [selectedCompany, reset]);

  // A central escolhida no formulário é quem recebe as integrações; em "minha central" ela vem do
  // provedor, porque o campo não é editável ali.
  const watchedCustomerId = useWatch({control, name: 'customerId'});
  const selectedCentralId = isMyCompany ?
    currentCompany?.customerId ?? 0 :
    watchedCustomerId ?? 0;

  const handleApiError = useCallback(
    (error: unknown) => {
      const apiError = error as {data?: string; status?: number};
      toast.errorStatus({data: apiError.data, status: apiError.status ?? 500});
    },
    [toast],
  );

  const navigateToList = useCallback(() => {
    dispatch({type: 'SET_ACTIVE_VIEW', payload: {view: 'list'}});
  }, [dispatch]);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      const attachment = attachmentRef.current ?? undefined;
      // When editing my own company, use the customDomain from the provider (not editable)
      const customDomain = isMyCompany ? currentCompany?.customDomain ?? '' : data.customDomain;
      // "My company" resolves the configuration by hostname, and that path does not carry the
      // owning customer: omitting the field is what keeps the server from dropping the link. From
      // the list the field always travels, and zero is the "no customer" choice, which unlinks.
      const customerId = isMyCompany ? undefined : data.customerId ?? 0;

      if (shouldCreate) {
        await createCompany({
          attachment,
          customerId,
          label: data.label,
          customDomain,
          hideLoginFooter: data.hideLoginFooter,
          theme: data.theme,
          androidAppUrl: data.androidAppUrl,
          iosAppUrl: data.iosAppUrl,
          customerAreaLink: data.customerAreaLink,
          customerAreaContact: data.customerAreaContact,
          assistanceService: data.assistanceService,
          monitoringCenterService: data.monitoringCenterService,
          monitoringCenterContact: data.monitoringCenterContact,
        });
        toast.success(intl.formatMessage({id: 'sharedSavedSuccess'}));
      } else {
        await updateCompany({
          // id is guaranteed by updateCompanySchema's yupResolver before onSubmit runs
          id: data.id!,
          attachment: attachment ?? undefined,
          logoUrl: selectedCompany?.logoUrl ?? '',
          customerId,
          label: data.label,
          customDomain,
          hideLoginFooter: data.hideLoginFooter,
          theme: data.theme,
          androidAppUrl: data.androidAppUrl,
          iosAppUrl: data.iosAppUrl,
          customerAreaLink: data.customerAreaLink,
          customerAreaContact: data.customerAreaContact,
          assistanceService: data.assistanceService,
          monitoringCenterService: data.monitoringCenterService,
          monitoringCenterContact: data.monitoringCenterContact,
        });
        toast.success(intl.formatMessage({id: 'sharedUpdatedSuccess'}));
      }

      navigateToList();
    } catch (err) {
      handleApiError(err);
    }
  };

  const onPickImage = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (!file) return;

    attachmentRef.current = file;
    setValue('attachment', file);

    const fileReader = new FileReader();
    fileReader.onload = function(e) {
      if (imageRef.current && e.target?.result) {
        imageRef.current.src = e.target.result as string;
      }
    };
    fileReader.readAsDataURL(file);
  }, [setValue]);

  const handleLogoClick = useCallback(() => {
    logoUrlRef.current?.click();
  }, []);

  const isLoading = isSubmitting || isCreatePending || isUpdatePending;
  const hasExistingLogo = selectedCompany?.logoUrl && !shouldCreate;

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FieldSet
        className="text-left border p-4"
        title={intl.formatMessage({id: 'sharedIdentification'})}
      >
        <div className="row">
          <div className="col-12 col-md-4 col-lg-3">
            <FormGroup className="custom-formGroup">
              <Label>
                <I18nMessage id="companyLogo" />
              </Label>
              <img
                ref={imageRef}
                src={
                  hasExistingLogo ?
                    selectedCompany.logoUrl :
                    `${process.env.PUBLIC_URL}/images/thumbnail.svg`
                }
                onClick={handleLogoClick}
                className="rounded img-thumbnail"
                alt="Company Logo"
                style={{cursor: 'pointer', width: '100%', height: 'auto', aspectRatio: '1 / 1', objectFit: 'contain', maxWidth: '300px'}}
              />
              <br />
              <Input
                innerRef={logoUrlRef}
                hidden
                type="file"
                accept="image/*"
                onChange={onPickImage}
              />
              {errors.attachment && (
                <span className="error">{errors.attachment.message}</span>
              )}
            </FormGroup>
          </div>

          <div className="col-12 col-md-8 col-lg-9">
            {!isMyCompany && (
              <Field name="customerId" label={intl.formatMessage({id: 'sharedCompany'})} type="text">
                <Controller
                  name="customerId"
                  control={control}
                  render={({field}) => (
                    <Input
                      type="select"
                      value={field.value ?? ''}
                      onChange={(event) => field.onChange(
                        event.target.value ? Number(event.target.value) : undefined,
                      )}
                    >
                      <option value="">{intl.formatMessage({id: 'sharedSelect'})}</option>
                      {centrals.map((central) => (
                        <option key={central.id} value={central.id}>{central.name}</option>
                      ))}
                    </Input>
                  )}
                />
              </Field>
            )}

            {!isMyCompany && (
              <Field name="label" label={intl.formatMessage({id: 'companyLabel'})} type="text">
                <Controller
                  name="label"
                  control={control}
                  render={({field}) => (
                    <TextInput {...field} placeholder={intl.formatMessage({id: 'companyLabel'})} />
                  )}
                />
                {errors.label && <span className={styles.fieldError}>{errors.label.message}</span>}
              </Field>
            )}

            {!isMyCompany && (
              <Field name="customDomain" label={intl.formatMessage({id: 'companyCustomDomain'})} type="text">
                <Controller
                  name="customDomain"
                  control={control}
                  render={({field}) => (
                    <TextInput {...field} placeholder={intl.formatMessage({id: 'companyCustomDomain'})} />
                  )}
                />
                {errors.customDomain && <span className={styles.fieldError}>{errors.customDomain.message}</span>}
              </Field>
            )}

            <Field name="theme" label={intl.formatMessage({id: 'companyTheme'})} type="text">
              <Controller
                name="theme"
                control={control}
                render={({field}) => (
                  <ColorPicker
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={intl.formatMessage({id: 'companyTheme'})}
                  />
                )}
              />
              {errors.theme && <span className={styles.fieldError}>{errors.theme.message}</span>}
            </Field>

            <div className="row">
              <div className="col-12 col-md-6">
                <Field name="androidAppUrl" label={intl.formatMessage({id: 'companyAndroidAppUrl'})} type="text">
                  <Controller
                    name="androidAppUrl"
                    control={control}
                    render={({field}) => (
                      <TextInput {...field} placeholder={intl.formatMessage({id: 'companyAndroidAppUrl'})} />
                    )}
                  />
                  {errors.androidAppUrl && <span className={styles.fieldError}>{errors.androidAppUrl.message}</span>}
                </Field>
              </div>
              <div className="col-12 col-md-6">
                <Field name="iosAppUrl" label={intl.formatMessage({id: 'companyIOSAppUrl'})} type="text">
                  <Controller
                    name="iosAppUrl"
                    control={control}
                    render={({field}) => (
                      <TextInput {...field} placeholder={intl.formatMessage({id: 'companyIOSAppUrl'})} />
                    )}
                  />
                  {errors.iosAppUrl && <span className={styles.fieldError}>{errors.iosAppUrl.message}</span>}
                </Field>
              </div>
            </div>

            <div className="row">
              <div className="col-12 col-md-6">
                <Field name="customerAreaLink" label={intl.formatMessage({id: 'companyCustomerAreaLink'})} type="text">
                  <Controller
                    name="customerAreaLink"
                    control={control}
                    render={({field}) => (
                      <TextInput {...field} placeholder={intl.formatMessage({id: 'companyCustomerAreaLink'})} />
                    )}
                  />
                  {errors.customerAreaLink && <span className={styles.fieldError}>{errors.customerAreaLink.message}</span>}
                </Field>
              </div>
              <div className="col-12 col-md-6">
                <Field name="customerAreaContact" label={intl.formatMessage({id: 'companyCustomerAreaContact'})} type="text">
                  <Controller
                    name="customerAreaContact"
                    control={control}
                    render={({field}) => (
                      <TextInput {...field} placeholder={intl.formatMessage({id: 'companyCustomerAreaContact'})} />
                    )}
                  />
                  {errors.customerAreaContact && <span className={styles.fieldError}>{errors.customerAreaContact.message}</span>}
                </Field>
              </div>
            </div>

            <div className="row">
              <div className="col-12 col-md-6">
                <Field name="monitoringCenterContact" label={intl.formatMessage({id: 'companyMonitoringCenterContact'})} type="text">
                  <Controller
                    name="monitoringCenterContact"
                    control={control}
                    render={({field}) => (
                      <TextInput {...field} placeholder={intl.formatMessage({id: 'companyMonitoringCenterContact'})} />
                    )}
                  />
                  {errors.monitoringCenterContact && <span className={styles.fieldError}>{errors.monitoringCenterContact.message}</span>}
                </Field>
              </div>
            </div>

            <div className={styles.togglesRow}>
              <Controller
                name="assistanceService"
                control={control}
                render={({field}) => (
                  <Toggle
                    checked={field.value}
                    onChange={field.onChange}
                    label={intl.formatMessage({id: 'companyAssistanceService'})}
                  />
                )}
              />
              <Controller
                name="monitoringCenterService"
                control={control}
                render={({field}) => (
                  <Toggle
                    checked={field.value}
                    onChange={field.onChange}
                    label={intl.formatMessage({id: 'companyMonitoringCenterService'})}
                  />
                )}
              />
              <Controller
                name="hideLoginFooter"
                control={control}
                render={({field}) => (
                  <Toggle
                    checked={field.value}
                    onChange={field.onChange}
                    label={intl.formatMessage({id: 'companyHideLoginFooter'})}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </FieldSet>
      {selectedCentralId > 0 && (
        <>
          <br />
          <FieldSet
            className="text-left border p-4"
            title={intl.formatMessage({id: 'sharedIntegrations'})}
          >
            <div className={styles.togglesRow}>
              <IntegrationAccessToggles
                owner="customer"
                ownerId={selectedCentralId}
                integrations={integrations}
              />
            </div>
          </FieldSet>
        </>
      )}
      <br />
      <div className={styles.buttonsContainer}>
        <Button variant="secondary" onClick={navigateToList} disabled={isLoading}>
          <span><I18nMessage id="sharedCancel" /></span>
        </Button>
        <Button disabled={isLoading} onClick={handleSubmit(onSubmit)}>
          <span><I18nMessage id="sharedSave" /></span>
        </Button>
      </div>
    </Form>
  );
};

export default CompanyFormView;
